import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { TreeTable } from "primereact/treetable";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import { useTranslation } from "react-i18next";

// Configuration for chunking and performance
const CHUNK_SIZE = 30;
const DEBOUNCE_DELAY = 100;
const MAX_INITIAL_EXPANSION = 0;
const RECURSION_LIMIT = 500; // Safety limit to prevent stack overflow

// Utility: Safe debounce
const debounce = (func, delay) => {
  let timeoutId;
  return function debounced(...args) {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      try {
        func.apply(this, args);
      } catch (e) {
        console.error("[CustomTreeTable] Debounced function error:", e);
      }
    }, delay);
  };
};

// Utility: Safe throttle
const throttle = (func, limit) => {
  let inThrottle = false;
  return function throttled(...args) {
    if (!inThrottle) {
      try {
        func.apply(this, args);
      } catch (e) {
        console.error("[CustomTreeTable] Throttled function error:", e);
      }
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

// Ultimate Robust expanded key getter
const getAllExpandedKeys = (nodes, maxLevel = MAX_INITIAL_EXPANSION) => {
  if (!Array.isArray(nodes) || nodes.length === 0) return {};

  const keys = {};
  const visited = new Set(); // Circular reference protection

  const traverse = (nodeList, currentLevel = 0) => {
    if (!Array.isArray(nodeList) || currentLevel > RECURSION_LIMIT) return;

    for (let i = 0; i < nodeList.length; i++) {
      const node = nodeList[i];
      if (!node || !node.key || visited.has(node)) continue;

      visited.add(node);
      if (
        node.children &&
        Array.isArray(node.children) &&
        node.children.length > 0
      ) {
        if (currentLevel < maxLevel) {
          keys[node.key] = true;
          traverse(node.children, currentLevel + 1);
        }
      }
    }
  };

  try {
    traverse(nodes);
  } catch (error) {
    console.error("[CustomTreeTable] Expansion calculation failed:", error);
  } finally {
    visited.clear();
  }

  return keys;
};

// Ultimate Robust data processor (Lazy Recursion + Infinite Loop Protection)
const processTreeDataWithChunks = (
  nodes,
  visibleChildrenMap,
  expandedKeys,
  depth = 0,
  visited = new Set(),
) => {
  if (!Array.isArray(nodes) || nodes.length === 0 || depth > RECURSION_LIMIT)
    return nodes;

  try {
    const result = [];
    const nodeCount = nodes.length;

    for (let i = 0; i < nodeCount; i++) {
      const node = nodes[i];
      if (!node || !node.key || visited.has(node)) {
        if (node) result.push(node);
        continue;
      }

      visited.add(node);
      const nodeKey = node.key;

      // If not expanded, return as is (Lazy recursion)
      if (!expandedKeys || !expandedKeys[nodeKey]) {
        result.push({ ...node, _depth: depth });
        continue;
      }

      if (
        node.children &&
        Array.isArray(node.children) &&
        node.children.length > 0
      ) {
        const totalChildren = node.children.length;
        const visibleCount = Math.max(
          CHUNK_SIZE,
          visibleChildrenMap?.[nodeKey] || CHUNK_SIZE,
        );
        const visibleChildren = node.children.slice(0, visibleCount);
        const hasMore = visibleCount < totalChildren;

        const processedChildren = processTreeDataWithChunks(
          visibleChildren,
          visibleChildrenMap,
          expandedKeys,
          depth + 1,
          visited,
        );

        const finalNode = { ...node, _depth: depth, children: processedChildren };

        if (hasMore) {
          finalNode.children.push({
            key: `${nodeKey}-load-more`,
            data: {
              isLoadMore: true,
              parentKey: nodeKey,
              remaining: totalChildren - visibleCount,
            },
            children: [],
          });
        }
        result.push(finalNode);
      } else {
        result.push({ ...node, _depth: depth });
      }
    }
    return result;
  } catch (error) {
    console.error("[CustomTreeTable] Processing error:", error);
    return nodes || [];
  }
};

const ReusableTreeTable = (props) => {
  // Defensive props destructuring
  const {
    data = [],
    isLoading = false,
    columns = [],
    tableStyle = { minWidth: "50rem" },
    paginator = false,
    loadingIcon = null,
    globalFilter = null,
    filterMode = "lenient",
    onRowToggle = () => { },
    ...rest
  } = props;

  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";

  const [expandedKeys, setExpandedKeys] = useState({});
  const [visibleChildrenMap, setVisibleChildrenMap] = useState({});

  const observersRef = useRef({});
  const timeoutRefs = useRef(new Set());
  const isMountedRef = useRef(true);
  const loadingKeysRef = useRef(new Set());

  // Track parent-child relationships safely
  const parentMap = useMemo(() => {
    const map = {};
    const visited = new Set();
    const traverse = (nodes, parentKey = null, depth = 0) => {
      if (!Array.isArray(nodes) || depth > RECURSION_LIMIT) return;
      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];
        if (!node || !node.key || visited.has(node)) continue;
        visited.add(node);
        map[node.key] = parentKey;
        if (node.children) traverse(node.children, node.key, depth + 1);
      }
    };
    try {
      if (data) traverse(data);
    } finally {
      visited.clear();
    }
    return map;
  }, [data]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Sync expanded keys on data change safely
  useEffect(() => {
    if (!isMountedRef.current || !data) return;
    try {
      setExpandedKeys((prev) => {
        const initial = getAllExpandedKeys(data, MAX_INITIAL_EXPANSION);
        return { ...prev, ...initial };
      });
    } catch (e) {
      console.error("[CustomTreeTable] Key sync failed");
    }
  }, [data]);

  // Robust load more handler
  const loadMoreChildren = useCallback((parentKey) => {
    if (
      !isMountedRef.current ||
      !parentKey ||
      loadingKeysRef.current.has(parentKey)
    )
      return;

    loadingKeysRef.current.add(parentKey);

    try {
      setVisibleChildrenMap((prev) => {
        const currentCount = prev[parentKey] || CHUNK_SIZE;
        return { ...prev, [parentKey]: currentCount + CHUNK_SIZE };
      });
    } catch (e) {
      console.error("[CustomTreeTable] Load state update failed");
    } finally {
      const timer = setTimeout(() => {
        if (loadingKeysRef.current) loadingKeysRef.current.delete(parentKey);
        if (timeoutRefs.current) timeoutRefs.current.delete(timer);
      }, 300);
      timeoutRefs.current.add(timer);
    }
  }, []);

  // Absolute cleanup
  useEffect(() => {
    return () => {
      // Disconnect all observers
      Object.keys(observersRef.current).forEach((key) => {
        if (observersRef.current[key]) observersRef.current[key].disconnect();
      });
      observersRef.current = {};

      // Clear all timeouts
      timeoutRefs.current.forEach((timer) => clearTimeout(timer));
      timeoutRefs.current.clear();

      if (loadingKeysRef.current) loadingKeysRef.current.clear();
    };
  }, []);

  const processedData = useMemo(() => {
    const visited = new Set();
    try {
      return processTreeDataWithChunks(
        data,
        visibleChildrenMap,
        expandedKeys,
        0,
        visited,
      );
    } finally {
      visited.clear();
    }
  }, [data, visibleChildrenMap, expandedKeys]);

  const createBodyRenderer = useCallback(
    (originalBody, col) => {
      return (rowData) => {
        if (isLoading) return <Skeleton width="100%" height="1.5rem" />;
        if (!rowData || !rowData.data) return null;

        try {
          if (rowData.data.isLoadMore) {
            if (!col?.expander) return null;

            const parentKey = rowData.data.parentKey;
            const remaining = rowData.data.remaining;
            const isLoadingNode = loadingKeysRef.current.has(parentKey);

            return (
              <div
                ref={(el) => {
                  if (el && parentKey && isMountedRef.current) {
                    if (!observersRef.current[rowData.key]) {
                      try {
                        const obs = new IntersectionObserver(
                          (entries) => {
                            if (
                              isMountedRef.current &&
                              entries[0]?.isIntersecting
                            )
                              loadMoreChildren(parentKey);
                          },
                          { rootMargin: "500px", threshold: 0.01 },
                        );
                        obs.observe(el);
                        observersRef.current[rowData.key] = obs;
                      } catch (e) { }
                    }

                    // Visibility fallback
                    const check = setTimeout(() => {
                      if (!isMountedRef.current || !el) return;
                      const rect = el.getBoundingClientRect();
                      if (
                        rect.top < (window.innerHeight || 1000) &&
                        rect.bottom > 0
                      ) {
                        if (!loadingKeysRef.current.has(parentKey))
                          loadMoreChildren(parentKey);
                      }
                      timeoutRefs.current.delete(check);
                    }, 150);
                    timeoutRefs.current.add(check);
                  } else if (!el && observersRef.current[rowData.key]) {
                    observersRef.current[rowData.key].disconnect();
                    delete observersRef.current[rowData.key];
                  }
                }}
                className={`text-primary italic py-2 cursor-pointer hover:underline ${isLoadingNode ? "opacity-50 pointer-events-none" : ""}`}
                onClick={() => !isLoadingNode && loadMoreChildren(parentKey)}
              >
                {isLoadingNode
                  ? "Loading..."
                  : `Loading ${Math.min(CHUNK_SIZE, remaining)} more of ${remaining} remaining...`}
              </div>
            );
          }

          if (typeof originalBody === "function") {
            return originalBody(rowData);
          }

          return col?.field ? (rowData.data[col.field] ?? "") : null;
        } catch (err) {
          console.error("[CustomTreeTable] Body render error:", err);
          return <span className="text-red-500">Error Rendering Cell</span>;
        }
      };
    },
    [loadMoreChildren, isLoading],
  );

  const renderedColumns = useMemo(() => {
    if (!Array.isArray(columns)) return null;
    return columns.map((col, idx) => (
      <Column
        key={col?.key || col?.field || `safe-col-${idx}`}
        {...col}
        body={createBodyRenderer(col?.body, col)}
        pt={{
          // root: { className: "w-full" },
          headerCell: {
            className: `!px-[0.5rem] !py-[0.4rem] !bg-gray-300 ${isRtl ? "text-right" : "text-left"
              } ${col?.headerCellClass || ""}`,
            ...col?.pt?.headerCell,
          },
          headerTitle: {
            className: `text-[13px] text-gray-900 !font-[600] whitespace-nowrap ${col?.headerTitleClass || ""
              }`,
          },
          bodyCell: {
            className: `!px-[0.5rem] !py-[0.2rem] text-12-gray text-gray-600 leading-[1rem] border-b !border-b-gray-200 bg-white ${isRtl ? "text-right" : "text-left"
              } whitespace-nowrap ${col?.bodyCellClass || ""}`,
            ...col?.pt?.bodyCell,
          },
          ...col?.pt,
        }}
      />
    ));
  }, [columns, createBodyRenderer, isRtl]);

  const handleToggle = useCallback(
    throttle((e) => {
      if (!isMountedRef.current) return;
      try {
        const next = e.value || {};
        const prev = expandedKeys;
        const added = Object.keys(next).filter((k) => !prev[k]);

        let final = { ...next };
        if (added.length > 0) {
          const newKey = added[0];
          const p = parentMap[newKey];
          Object.keys(final).forEach((k) => {
            if (k !== newKey && parentMap[k] === p && final[k]) delete final[k];
          });
        }

        const collapsed = Object.keys(prev).filter((k) => prev[k] && !final[k]);
        if (collapsed.length > 0) {
          setVisibleChildrenMap((curr) => {
            const upd = { ...curr };
            collapsed.forEach((k) => delete upd[k]);
            return upd;
          });
        }
        setExpandedKeys(final);
      } catch (err) {
        console.error("[CustomTreeTable] Toggle failed");
      }
    }, 100),
    [expandedKeys, parentMap],
  );

  const togglerTemplate = (node, options) => {
    const depthSpacing = node._depth ? node._depth * 2 : 0;
    const spacingStyle = isRtl
      ? { marginRight: `${depthSpacing}rem` }
      : { marginLeft: `${depthSpacing}rem` };

    if (!node.children || node.children.length === 0 || isLoading) {
      return (
        <span
          className="p-treetable-toggler"
          style={{
            visibility: "hidden",
            display: "inline-block",
            width: "1.25rem",
            ...spacingStyle,
          }}
        ></span>
      );
    }

    return (
      <button
        type="button"
        className="p-treetable-toggler p-link inline"
        onClick={options.onClick}
        tabIndex={-1}
        style={{ color: "inherit", ...spacingStyle }}
      >
        <span
          className={`p-treetable-toggler-icon pi ${options.expanded ? "pi-minus" : "pi-plus"
            } text-[12px] w-5 h-5 flex items-center justify-center text-primary transition-all duration-200 active:scale-90`}
          aria-hidden="true"
        ></span>
      </button>
    );
  };

  return (
    <div className="custom-tree-table-container w-full overflow-hidden border border-gray-300 rounded-[4px]">
      <TreeTable
        value={isLoading ? Array(7).fill({}).map((_, i) => ({ key: `skeleton-${i}`, data: {} })) : processedData}
        loadingIcon={loadingIcon}
        tableStyle={tableStyle}
        togglerTemplate={togglerTemplate}
        paginator={paginator}
        globalFilter={globalFilter}
        filterMode={filterMode}
        expandedKeys={expandedKeys}
        onToggle={handleToggle}
        onRowToggle={onRowToggle}
        emptyMessage={<div className="flex flex-col items-center justify-center text-center p-1">{t('no_available_options')}</div>}
        pt={{
          root: { className: "custom-table" },
          loadingIcon: { className: "text-primary" },
          loadingOverlay: { className: "bg-[rgba(0,0,0,0.2)]" },
          emptyMessage: { className: 'text-center p-4' },
          wrapper: {
            className:
              "overflow-x-auto",
          },
          table: { className: "rounded-md overflow-hidden" },
        }}
        {...rest}
      >
        {renderedColumns}
      </TreeTable>
    </div>
  );
};

export default React.memo(ReusableTreeTable);
